import { useState, useRef, useMemo } from "react";
import {
  DndContext,
  DragEndEvent,
  DragOverEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  useSensor,
  useSensors,
  closestCenter,
  pointerWithin,
  MeasuringStrategy,
  CollisionDetection,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";
import { LayoutType } from "./LayoutContainer";
import DroppableColumn from "./DroppableColumn";
import SortableCard from "./SortableCard";
import FixedCard from "@/components/common/FixedCard";
// Icons for Fixed Cards
import DemographicsIcon from "@/assets/icons/demographics.svg";
import CareManagementIcon from "@/assets/icons/caremanagement.svg";

export interface LayoutCard {
  instanceId: string;      // Unique ID per card instance — used as the dnd-kit drag ID
  id: string;              // Card type identifier (e.g. "ADT_ALERTS") — used for logic/rendering
  label: string;
  preferredColumn?: string; // Column the user last placed this card in; drives layout-switch placement
}

interface EditLayoutPageProps {
  layout: LayoutType;
  columns: Record<string, LayoutCard[]>;
  setColumns: React.Dispatch<React.SetStateAction<Record<string, LayoutCard[]>>>;
}

export const LAYOUT_COLUMN_COUNT: Record<LayoutType, number> = {
  "100": 1,
  "50-50": 2,
  "3-column": 3,
  "70-30": 2,
  "30-70": 2,
};

// flex grow values — matches LayoutContainer config
const LAYOUT_GRID: Record<LayoutType, string> = {
  "100": "1fr",
  "50-50": "1fr 1fr",
  "3-column": "1fr 1fr 1fr",
  "70-30": "7fr 3fr",
  "30-70": "3fr 7fr",
};

const columnId = (index: number) => `col-${index + 1}`;

const findColumnOfCard = (
  columns: Record<string, LayoutCard[]>,
  instanceId: string
): string | null => {
  for (const [colId, cards] of Object.entries(columns)) {
    if (cards.some((c) => c.instanceId === instanceId)) return colId;
  }
  return null;
};

const CARD_CONFIGS: Record<string, any> = {
  ADT_ALERTS: {
    type: "table",
    dataKey: "ADT_ALERTS_DATA",
    columns: ["eventDate", "eventType", "eventFacility"],
  },
  BEHAVIORAL_HEALTH_DIAGNOSES: {
    type: "tickList",
    dataKey: "BEHAVIORAL_HEALTH_DATA",
  },
  SDOH_BARRIERS: {
    type: "tickList",
    dataKey: "SDOH_BARRIERS_DATA",
  },
  TWELVE_MONTH_COST_UTILIZATION: {
    type: "keyValue",
    dataKey: "COST_UTILIZATION_DATA",
  },
  MEDICAL_DIAGNOSES: {
    type: "tickList",
    dataKey: "MEDICAL_DIAGNOSES_DATA",
  },
  MEDICATION_FILL_HISTORY: {
    type: "table",
    dataKey: "MEDICATION_FILL_HISTORY_DATA",
    columns: ["drug", "fillDate", "daysSupply", "prescriber", "pharmacy", "class"],
  },
  IMMUNIZATIONS: {
    type: "table",
    dataKey: "IMMUNIZATION_DATA",
    columns: ["vaccine", "lastDose"],
  },
  REFERRALS: {
    type: "table",
    dataKey: "REFERRALS_DATA",
    columns: ["referralDate", "sendingFacility", "receivingFacility", "referralType", "referralStatus"],
  },
  CARE_GAPS: {
    type: "table",
    dataKey: "CARE_GAPS_DATA",
    columns: ["metricDescription", "metricStatus"],
  },
  RECENT_VISITS_ENCOUNTERS: {
    type: "table",
    dataKey: "RECENT_VISITS_DATA",
    columns: ["date", "type", "facility", "diagnosis"],
  },
  RISK_STRATIFICATION: {
    type: "table",
    dataKey: "RISK_STRATIFICATION_DATA",
    columns: ["riskDriver", "trend"],
  },
  HEALTH_PLAN: {
    type: "keyValue",
    dataKey: "HEALTHPLAN_DATA",
  },
  PCP: {
    type: "keyValue",
    dataKey: "PCP_DATA",
  },
};

const EditLayoutPage: React.FC<EditLayoutPageProps> = ({ layout, columns, setColumns }) => {
  const columnCount = LAYOUT_COLUMN_COUNT[layout];

  const [activeCard, setActiveCard] = useState<LayoutCard | null>(null);
  const layoutContainerRef = useRef<HTMLDivElement>(null);

  // Modifier that clamps the drag transform so the card cannot leave the
  // layout container's visible bounds. Works with DragOverlay: when the
  // overlay is active, dnd-kit passes the overlay's rect as `draggingNodeRect`.
  const restrictToContainer = useMemo(
    () =>
      ({
        transform,
        draggingNodeRect,
      }: {
        transform: { x: number; y: number; scaleX: number; scaleY: number };
        draggingNodeRect: { left: number; right: number; top: number; bottom: number } | null;
        [key: string]: unknown;
      }) => {
        if (!layoutContainerRef.current || !draggingNodeRect) return transform;

        const { left, right, top, bottom } =
          layoutContainerRef.current.getBoundingClientRect();

        return {
          ...transform,
          x: Math.min(
            Math.max(transform.x, left - draggingNodeRect.left),
            right - draggingNodeRect.right
          ),
          y: Math.min(
            Math.max(transform.y, top - draggingNodeRect.top),
            bottom - draggingNodeRect.bottom
          ),
        };
      },
    []
  );

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  const handleDragStart = ({ active }: DragStartEvent) => {
    const sourceCol = findColumnOfCard(columns, active.id as string);
    if (!sourceCol) return;
    const card = columns[sourceCol].find((c) => c.instanceId === active.id) ?? null;
    setActiveCard(card);
    document.body.classList.add("dragging-active");
  };

  const handleDragOver = ({ active, over }: DragOverEvent) => {
    if (!over) return;

    const activeId = active.id as string;
    const overId = over.id as string;

    setColumns((prev) => {
      // Derive sourceCol/targetCol from `prev` (latest state), not the stale closure.
      // Without this, a second dragOver firing before React re-renders would compute
      // sourceCol from old state and splice the wrong card out of the source column.
      const sourceCol = findColumnOfCard(prev, activeId);
      const targetCol =
        findColumnOfCard(prev, overId) ??
        (overId.startsWith("col-") ? overId : null);

      if (!sourceCol || !targetCol || sourceCol === targetCol) return prev;
      if (!prev[targetCol]) return prev;

      const sourceCards = [...prev[sourceCol]];
      const targetCards = [...prev[targetCol]];
      const cardIndex = sourceCards.findIndex((c) => c.instanceId === activeId);
      if (cardIndex === -1) return prev;

      const [moved] = sourceCards.splice(cardIndex, 1);
      const overIndex = targetCards.findIndex((c) => c.instanceId === overId);
      targetCards.splice(overIndex >= 0 ? overIndex : targetCards.length, 0, moved);

      return { ...prev, [sourceCol]: sourceCards, [targetCol]: targetCards };
    });
  };

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    setActiveCard(null);
    document.body.classList.remove("dragging-active");

    const activeId = active.id as string;

    setColumns((prev) => {
      // Find the card's actual column in the latest state — handleDragOver may have
      // already moved it cross-column before dragEnd fires.
      const currentCol = findColumnOfCard(prev, activeId);
      if (!currentCol) return prev;

      // Same-column reorder: only when over resolves to a different card in the same column.
      let reordered = { ...prev };
      if (over && over.id !== active.id) {
        const cards = prev[currentCol];
        const oldIndex = cards.findIndex((c) => c.instanceId === activeId);
        const newIndex = cards.findIndex((c) => c.instanceId === over.id);
        if (oldIndex !== -1 && newIndex !== -1) {
          reordered = {
            ...reordered,
            [currentCol]: arrayMove(prev[currentCol], oldIndex, newIndex),
          };
        }
      }

      // Always stamp preferredColumn to the card's final resting column.
      // This must run unconditionally — skipping it (e.g. when over is null or
      // over.id === active.id) would leave preferredColumn pointing at the old
      // column, so the next layout switch would misplace the card.
      return Object.fromEntries(
        Object.entries(reordered).map(([colId, colCards]) => [
          colId,
          colCards.map((card) =>
            card.instanceId === activeId
              ? { ...card, preferredColumn: colId }
              : card
          ),
        ])
      ) as Record<string, LayoutCard[]>;
    });
  };

  const customCollisionDetection: CollisionDetection = (args) => {
    // First, let's see if there are any collisions with the pointer
    const pointerCollisions = pointerWithin(args);

    if (pointerCollisions.length > 0) {
      return pointerCollisions;
    }

    // Fall back to closest center for items nearby
    return closestCenter(args);
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={customCollisionDetection}
      measuring={{
        droppable: {
          strategy: MeasuringStrategy.Always,
        },
      }}
      modifiers={[restrictToContainer]}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
      onDragCancel={() => {
        setActiveCard(null);
        document.body.classList.remove("dragging-active");
      }}
    >
      <div className="flex-1 flex flex-col w-full">
        <div className="flex flex-col w-full gap-4 pb-4 h-full">
        {/* Fixed Cards Row aligned with columns */}
        <div 
          className="grid gap-4 w-full" 
          style={{ gridTemplateColumns: LAYOUT_GRID[layout] }}
        >
          {Array.from({ length: columnCount }, (_, index) => {
            return (
              <div key={`fixed-col-${index}`} className="flex flex-col gap-4 min-w-0">
                {index === 0 && (
                  <FixedCard title="Demographics" icon={DemographicsIcon} />
                )}
                {columnCount === 1 && index === 0 && (
                  <FixedCard title="Care Management" icon={CareManagementIcon} />
                )}
                {columnCount > 1 && index === 1 && (
                  <FixedCard title="Care Management" icon={CareManagementIcon} />
                )}
              </div>
            );
          })}
        </div>

        {/* Draggable Columns Area — ref used by the restrictToContainer modifier */}
        <div
          ref={layoutContainerRef}
          className="grid gap-4 w-full flex-1"
          style={{ gridTemplateColumns: LAYOUT_GRID[layout] }}
        >
          {Array.from({ length: columnCount }, (_, index) => {
            const colId = columnId(index);
            const cards = columns[colId] ?? [];
            const hasCards = cards.length > 0;
            return (
              <DroppableColumn
                key={colId}
                id={colId}
                hasCards={hasCards}
              >
                <SortableContext
                  items={cards.map((c) => c.instanceId)}
                  strategy={verticalListSortingStrategy}
                >
                  {cards.map((card) => {
                    const config = CARD_CONFIGS?.[card.id] ?? {};
                    return (
                      <SortableCard
                        key={card.instanceId}
                        card={card}
                        showStar={false}
                        {...config}
                      />
                    );
                  })}
                </SortableContext>
              </DroppableColumn>
            );
          })}
        </div>
      </div>
    </div>

      <DragOverlay>
        {activeCard ? (
          <SortableCard 
            card={activeCard} 
            isDragOverlay 
            showStar={false}
            {...(CARD_CONFIGS?.[activeCard?.id] ?? {})}
          />
        ) : null}
      </DragOverlay>
    </DndContext>
  );
};

export default EditLayoutPage;

